import { readJson, writeJson } from '../../core/storage/local';

/**
 * Live Ghanaian stations from the public radio-browser.info directory (community-run, free, no key).
 * Streams play straight from each station's own servers. Cached for a day.
 */
export interface LiveStation {
  id: string;
  name: string;
  url: string;
  detail: string;
}

const CACHE_KEY = 'trotrorun.liveStations.v1';
const DAY_MS = 86_400_000;
const MIRRORS = ['de1', 'de2', 'fi1', 'nl1', 'at1'];
const PLAYABLE = new Set(['MP3', 'AAC', 'AAC+', 'OGG']);

interface DirectoryEntry {
  stationuuid: string;
  name: string;
  url_resolved: string;
  codec: string;
  tags: string;
  state: string;
}

async function fromMirror(mirror: string): Promise<DirectoryEntry[] | null> {
  try {
    const url = `https://${mirror}.api.radio-browser.info/json/stations/bycountrycodeexact/GH?hidebroken=true&order=clickcount&reverse=true&limit=60`;
    const response = await fetch(url, { signal: AbortSignal.timeout(6000) });
    return response.ok ? ((await response.json()) as DirectoryEntry[]) : null;
  } catch {
    return null;
  }
}

function playableHere(entry: DirectoryEntry): boolean {
  const secureOnly = location.protocol === 'https:';
  return PLAYABLE.has(entry.codec.toUpperCase()) && (!secureOnly || entry.url_resolved.startsWith('https:'));
}

export async function loadLiveStations(): Promise<LiveStation[]> {
  const cached = readJson<{ at: number; stations: LiveStation[] }>(CACHE_KEY);
  if (cached && Date.now() - cached.at < DAY_MS && cached.stations.length > 0) return cached.stations;
  for (const mirror of MIRRORS) {
    const entries = await fromMirror(mirror);
    if (!entries) continue;
    const seen = new Set<string>();
    const stations = entries.filter(playableHere).filter((e) => {
      const key = e.name.trim().toLowerCase();
      return !seen.has(key) && seen.add(key);
    }).map((e) => ({
      id: e.stationuuid,
      name: e.name.trim(),
      url: e.url_resolved,
      detail: [e.state, e.tags.split(',').slice(0, 2).join(' · ')].filter(Boolean).join(' · ') || 'Live from Ghana',
    }));
    writeJson(CACHE_KEY, { at: Date.now(), stations });
    return stations;
  }
  return [];
}
